// @story HIR-HR16
// MessagesSent — "what message went to whom" (the owner, 28 Oct 2026): every
// message Hiring handed to Messages for this school in a period, counted per
// message by email and WhatsApp, and listed per applicant with when and who
// pressed it. Data from GET /api/dashboard/messages-sent. Delivery (arrived or
// not) comes later from Messages' feed; the one known loss is marked.
//
// Drawn as the other EquaOS apps are (owner, 29 Oct 2026): the page kit and
// the design system only.

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { Button, Card, EmptyState, Icon, Input, StatusPill, Table } from '../../ds';
import { Page, PageHeader, StatRow, Stat, Section, Loading, Note } from '../../components/page';

const PERIODS = [
  { days: 1, label: 'Today' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 365, label: 'A year' },
];

const when = (iso) => (iso
  ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' })
  : '—');

const channelText = (x) => x.channels.map((c) => (c === 'email' ? 'Email' : `WhatsApp${x.whatsappNotDelivered ? ' (not delivered)' : ''}`)).join(' + ');

function download(items) {
  const head = ['Applicant', 'Role', 'Message', 'Channels', 'Email', 'Mobile', 'When', 'Sent by'];
  const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = items.map((x) => [x.name, x.role, x.label, channelText(x), x.email, x.phone, when(x.at), x.sentBy].map(cell).join(','));
  const blob = new Blob([[head.map(cell).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'messages-sent.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/* `embedded`: inside the Messages page, which has the page's title. */
export default function MessagesSent({ embedded = false }) {
  // Inside Messages, the same 30 days as the "Sent" block above it.
  const [days, setDays] = useState(embedded ? 30 : 7);
  // A link may name the message to show (a communication's "Who got it").
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState(searchParams.get('message') || '');
  const [q, setQ] = useState('');
  const [st, setSt] = useState({ phase: 'loading' });

  const load = useCallback(() => {
    setSt((s) => ({ ...s, phase: 'loading' }));
    api.get('/dashboard/messages-sent', { params: { days, message: message || undefined, q: q || undefined } })
      .then((r) => setSt({ phase: 'ready', data: r.data }))
      .catch((e) => setSt({ phase: 'error', msg: e?.response?.data?.message || 'Could not load the messages sent.' }));
  }, [days, message, q]);

  useEffect(() => { load(); }, [load]);

  const d = st.data;
  const loading = st.phase === 'loading';

  // The period, and a refresh: the page's actions, or a row of their own inside Messages.
  const controls = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {PERIODS.map((p) => (
        <Button key={p.days} size="sm" variant={days === p.days ? 'primary' : 'secondary'} aria-pressed={days === p.days} onClick={() => setDays(p.days)}>{p.label}</Button>
      ))}
      <Button size="sm" variant="ghost" title="Refresh" aria-label="Refresh" icon={<Icon name="arrow-clockwise" spin={loading} />} onClick={load} />
    </div>
  );

  const messageColumns = [
    { key: 'label', label: 'Message', render: (m) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-1)', fontWeight: message === m.message ? 600 : 400 }}>
        {m.label}{message === m.message && <StatusPill tone="info" dot={false}>shown below</StatusPill>}
      </span>
    ) },
    { key: 'people', label: 'People', render: (m) => <span className="num">{m.people}</span> },
    { key: 'emails', label: 'Emails', render: (m) => <span className="num">{m.emails}</span> },
    { key: 'whatsapps', label: 'WhatsApps', render: (m) => (
      <span><span className="num">{m.whatsapps - m.whatsappsNotDelivered}</span>{m.whatsappsNotDelivered ? <span style={{ color: 'var(--status-warning-text)', fontSize: 'var(--fs-caption)' }}> ({m.whatsappsNotDelivered} not delivered)</span> : null}</span>
    ) },
    { key: 'last', label: 'Last sent', render: (m) => <span style={{ color: 'var(--text-muted)' }}>{when(m.lastAt)}</span> },
  ];
  const itemColumns = [
    { key: 'name', label: 'Applicant', render: (x) => (
      <div>
        {x.jobId
          ? <a href={`/jobs/${x.jobId}/reports/${x.candidateId}`} style={{ color: 'var(--text-1)', fontWeight: 500, textDecoration: 'none' }}>{x.name}</a>
          : <span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{x.name}</span>}
        <small className="num" style={{ display: 'block', color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>{[x.email, x.phone].filter(Boolean).join(' · ')}</small>
      </div>
    ) },
    { key: 'role', label: 'Role', render: (x) => x.role },
    { key: 'label', label: 'Message', render: (x) => x.label },
    { key: 'by', label: 'By', render: (x) => <span style={x.whatsappNotDelivered ? { color: 'var(--status-warning-text)' } : undefined}>{channelText(x)}</span> },
    { key: 'at', label: 'When', render: (x) => <span style={{ color: 'var(--text-muted)' }}>{when(x.at)}</span> },
    { key: 'sentBy', label: 'Sent by', render: (x) => x.sentBy },
  ];

  const body = (
    <>
      {embedded && controls}

      {loading && !d && <Card><Loading label="Loading the messages sent" /></Card>}
      {st.phase === 'error' && <Note tone="danger">{st.msg}</Note>}

      {d && (
        <>
          <StatRow>
            <Stat label="Emails sent" value={d.totals.emails} hint={PERIODS.find((p) => p.days === days)?.label} />
            <Stat label="WhatsApps sent" value={d.totals.whatsapps - d.totals.whatsappsNotDelivered}
              hint={d.totals.whatsappsNotDelivered ? `plus ${d.totals.whatsappsNotDelivered} tried and not delivered` : PERIODS.find((p) => p.days === days)?.label}
              tone={d.totals.whatsappsNotDelivered ? 'warning' : undefined} />
          </StatRow>

          <Section title="By message" subtitle="open one to see only who got it" padding="20px 0 8px">
            {d.messages.length
              ? <Table columns={messageColumns} rows={d.messages} getRowId={(m) => m.message} onRowClick={(m) => setMessage(message === m.message ? '' : m.message)} />
              : <EmptyState icon={<Icon name="paper-plane" />} title="Nothing was sent in this period." description="Choose a longer period above to see earlier messages." />}
          </Section>

          <Section
            title={`Who got what${message ? ` — ${d.messages.find((m) => m.message === message)?.label || ''}` : ''}`}
            subtitle={`${d.total}${d.truncated ? `, newest ${d.items.length} shown` : ''}`}
            padding="20px 0 8px"
            action={(
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ width: 220 }}>
                  <Input id="messages-sent-find" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an applicant" icon={<Icon name="magnifying-glass" />} />
                </div>
                {message && <Button size="sm" variant="ghost" onClick={() => setMessage('')}>Show every message</Button>}
                <Button size="sm" variant="secondary" icon={<Icon name="download" />} onClick={() => download(d.items)} disabled={!d.items.length}>Download</Button>
              </div>
            )}>
            <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
              <Table columns={itemColumns} rows={d.items.map((x, i) => ({ ...x, _row: `${x.candidateId}-${i}` }))} getRowId={(x) => x._row} />
            </div>
            <p style={{ margin: '12px 20px 4px', fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>{d.note}</p>
          </Section>
        </>
      )}
    </>
  );

  if (embedded) return body;
  return (
    <Page>
      <PageHeader title="Messages sent" subtitle="What went to which applicant, by email or WhatsApp, at this school." actions={controls} />
      {body}
    </Page>
  );
}
